import { AnalyticsPage } from "@/components/analytics/AnalyticsPage";
import { Icon } from "@/components/analytics/Icon";
import { AvatarsTable, Donut, Kpi, OriginBars, WeeklyChart } from "@/components/analytics/pieces";
import { fmt, type OriginKey } from "@/lib/analytics/demo-data";
import { useAnalyticsData } from "@/lib/analytics/range";

export function AnalyticsOverviewPage() {
  const { avatars: AVATARS } = useAnalyticsData();
  const totalSessions = AVATARS.reduce((s, a) => s + a.sessions, 0);
  const totalMin = AVATARS.reduce((s, a) => s + a.streamingTotal, 0);
  const wComp =
    totalSessions > 0
      ? Math.round(AVATARS.reduce((s, a) => s + a.completionRate * a.sessions, 0) / totalSessions)
      : null;
  const scA = AVATARS.filter((a) => a.is_scorable);
  const scSess = scA.reduce((s, a) => s + a.sessions, 0);
  const avgScore =
    scSess > 0
      ? Math.round(scA.reduce((s, a) => s + (a.avgScore ?? 0) * a.sessions, 0) / scSess)
      : null;
  const cert = scA.find((a) => typeof a.passRate === "number");

  const origAgg: Record<OriginKey, number> = { workspace: 0, scorm: 0, public_link: 0 };
  AVATARS.forEach((a) =>
    (Object.keys(origAgg) as OriginKey[]).forEach((k) => (origAgg[k] += a.origin[k])),
  );

  return (
    <AnalyticsPage
      title="Analytics overview · Live Avatars"
      description="Usage, completion and certification analytics for every live avatar across Powtoon workspace, SCORM / LMS and public access channels."
    >
      <div className="page-head">
        <div>
          <h1>Analytics overview</h1>
          <p>
            How your live avatars are being used across every channel. Scoring metrics apply only to
            avatars built with a scorable schema.
          </p>
        </div>
      </div>

      <div className="kpi-row">
        <Kpi
          icon="forum"
          label="Total sessions"
          value={fmt(totalSessions)}
          sub={`${AVATARS.length} live avatars`}
        />
        <Kpi
          icon="schedule"
          label="Streaming minutes"
          value={fmt(totalMin)}
          unit="min"
          sub="all avatars combined"
        />
        <Kpi
          icon="task_alt"
          label="Av. pass rate"
          value={wComp ?? "—"}
          unit={wComp !== null ? "%" : undefined}
          sub="weighted by sessions"
        />
        <Kpi
          icon="grade"
          label="Avg score"
          value={avgScore ?? "—"}
          unit={avgScore !== null ? "%" : undefined}
          sub="scorable avatars only"
        />
      </div>


      <div className="panel">
        <div className="panel-h">
          <Icon name="show_chart" />
          <h3>Weekly activity</h3>
          <div className="spacer" />
          <div className="chart-legend">
            <span className="cl">
              <span className="ln" style={{ background: "#3A57FA" }} />
              Sessions
            </span>
            <span className="cl">
              <span className="ln" style={{ background: "#854CE0" }} />
              Unique learners
            </span>
          </div>
        </div>
        <WeeklyChart />
      </div>

      <div className="grid-2">
        <div className="panel">
          <div className="panel-h">
            <Icon name="hub" />
            <h3>Sessions by channel</h3>
            <div className="spacer" />
            <span className="panel-note">{fmt(totalSessions)} total</span>
          </div>
          <OriginBars data={origAgg} total={totalSessions} />
        </div>

        <div className="panel">
          <div className="panel-h">
            <Icon name="military_tech" />
            <h3>Certification health</h3>
          </div>
          {cert ? (
            <div className="donut-wrap">
              <Donut pass={cert.passRate!} />
              <div className="legend">
                <div className="li">
                  <span className="dot" style={{ background: "#3A57FA" }} />
                  <div>
                    <b>{cert.passRate}% passed</b>
                    <div className="sub">
                      {Math.round((scSess * cert.passRate!) / 100)} learners{cert.pass_criteria != null ? ` ≥ ${cert.pass_criteria}%` : " passed"}
                    </div>
                  </div>
                </div>
                <div className="li">
                  <span className="dot" style={{ background: "#F5B9C2" }} />
                  <div>
                    <b>{100 - cert.passRate!}% did not pass</b>
                    <div className="sub">{cert.pass_criteria != null ? `below the ${cert.pass_criteria}% threshold` : "did not pass"}</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="note">
              <Icon name="info" /> No scoring data in this range. Scores are produced by the
              assessment pipeline and aren’t available for these sessions yet.
            </div>
          )}
        </div>
      </div>

      <div className="panel-h" style={{ marginTop: 4 }}>
        <Icon name="groups" />
        <h3>Live avatars</h3>
      </div>
      <AvatarsTable />
    </AnalyticsPage>
  );
}